import { useState, useRef, useEffect, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc.js";
import { BANK_KIND_LABEL_KEYS, bankLabelFallback, parseBankLabel } from "../../lib/bank-label.js";
import { useModals } from "../../components/common/ModalsProvider.js";
import { fmt, TIPO_LABELS, TYPE_OPTS, COMPANY_OPTS, ACCOUNT_OPTS } from "./finance-utils.js";
import { useMonth } from "./finance-month.js";
import { useFinanceCanEdit } from "./finance-access.js";
import { useT } from "../../i18n/index.js";

const PAGE_SIZE = 30;

type Filters = {
  company?: string;
  type?: string;
  account?: string;
  search: string;
};

export function LancamentosPage() {
  const qc = useQueryClient();
  const modals = useModals();
  const canEdit = useFinanceCanEdit();
  const t = useT();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({ search: "" });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [distId, setDistId] = useState<string | null>(null);
  const [showNovo, setShowNovo] = useState(false);
  const [showRegras, setShowRegras] = useState(false);

  const { month, setMonth, months } = useMonth();
  const { data: monthly = [] } = trpc.finance.dashboard.monthly.useQuery();

  function selectMonth(m: string) {
    setMonth(m);
    setPage(1);
    setSelected(new Set());
  }

  const { data, isLoading } = trpc.finance.transactions.list.useQuery({
    page,
    pageSize: PAGE_SIZE,
    month,
    company: (filters.company as any) || undefined,
    type: (filters.type as any) || undefined,
    account: (filters.account as any) || undefined,
    search: filters.search || undefined,
  });

  const updateRow = trpc.finance.transactions.update.useMutation({
    onSuccess: () => qc.invalidateQueries(),
  });
  const deleteRow = trpc.finance.transactions.delete.useMutation({
    onSuccess: () => qc.invalidateQueries(),
  });
  const resetRow = trpc.finance.transactions.reset.useMutation({
    onSuccess: () => qc.invalidateQueries(),
  });
  const bulkUpdate = trpc.finance.transactions.bulkUpdate.useMutation({
    onSuccess: () => {
      qc.invalidateQueries();
      setSelected(new Set());
    },
  });
  const dedup = trpc.finance.transactions.removeDuplicates.useMutation({
    onSuccess: (r) => {
      qc.invalidateQueries();
      void modals.alert({ message: `${r.removed} ${t("financeEntries.duplicatesRemoved")}` });
    },
  });
  const clearPend = trpc.finance.transactions.clearPending.useMutation({
    onSuccess: () => qc.invalidateQueries(),
  });
  const applyRules = trpc.finance.rules.applyToPending.useMutation({
    onSuccess: (r) => {
      qc.invalidateQueries();
      void modals.alert({
        message: `✓ ${r.conciliated} ${t("financeEntries.reconciledByRule")} + ${r.defaulted} ${t("financeEntries.reconciledToCompanyAccount")}`,
      });
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pages = Math.ceil(total / PAGE_SIZE);

  const ACCOUNTS_LIST = ACCOUNT_OPTS.map((o) => o.value);
  const f = (v: number) => fmt(v);
  const monthLabel = (m: string) => {
    const [y, mo] = m.split("-");
    return (
      ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"][
        Number(mo) - 1
      ] +
      "/" +
      (y ?? "").slice(2)
    );
  };
  const monthRow = monthly.find((m) => m.month === month);
  const finalBal: Record<string, number> = (monthRow?.contas as any) ?? {};

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };
  const toggleAll = (checked: boolean) => {
    setSelected(checked ? new Set(items.map((r) => r.id)) : new Set());
  };

  // Bulk state
  const [bulkCompany, setBulkCompany] = useState("");
  const [bulkType, setBulkType] = useState("");
  const [bulkIn, setBulkIn] = useState("");
  const [bulkOut, setBulkOut] = useState("");

  function applyBulk() {
    if (!bulkCompany && !bulkType && !bulkIn && !bulkOut) return;
    bulkUpdate.mutate({
      ids: [...selected],
      company: (bulkCompany as any) || undefined,
      type: (bulkType as any) || undefined,
      accountIn: (bulkIn as any) || undefined,
      accountOut: (bulkOut as any) || undefined,
    });
    setBulkCompany("");
    setBulkType("");
    setBulkIn("");
    setBulkOut("");
  }

  return (
    <div>
      {/* Seletor de mês */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: "var(--text3)",
            textTransform: "uppercase",
            letterSpacing: ".06em",
          }}
        >
          {t("financeEntries.monthLabel")}
        </span>
        <select
          value={month}
          onChange={(e) => selectMonth(e.target.value)}
          style={{
            background: "var(--bg3)",
            border: "1px solid var(--border2)",
            color: "var(--text)",
            fontFamily: "var(--sans)",
            fontSize: 13,
            padding: "7px 10px",
            borderRadius: "var(--r)",
            cursor: "pointer",
          }}
        >
          {months.map((m) => (
            <option key={m} value={m}>
              {monthLabel(m)}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: "var(--text3)" }}>
          {total}{" "}
          {total !== 1 ? t("financeEntries.entriesPlural") : t("financeEntries.entrySingular")}{" "}
          {t("financeEntries.inMonth")} {monthLabel(month)}
        </span>
      </div>

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        {canEdit && (
          <>
            <button className="btn primary" onClick={() => setShowNovo(true)}>
              + {t("financeEntries.newBtn")}
            </button>
            <button className="btn danger" onClick={() => dedup.mutate()}>
              {t("financeEntries.removeDuplicatesBtn")}
            </button>
            <button
              className="btn danger"
              style={{ opacity: 0.7 }}
              onClick={async () => {
                if (
                  await modals.confirm({
                    title: t("financeEntries.confirmTitle"),
                    message: t("financeEntries.confirmClearPending"),
                    tone: "danger",
                  })
                )
                  clearPend.mutate();
              }}
            >
              {t("financeEntries.clearPendingBtn")}
            </button>
            <button className="btn" onClick={() => setShowRegras(true)}>
              ⚙ {t("financeEntries.rulesBtn")}
            </button>
            <button
              className="btn"
              style={{ color: "var(--accent)", borderColor: "rgba(79,142,247,.3)" }}
              onClick={() => applyRules.mutate()}
              disabled={applyRules.isPending}
            >
              {applyRules.isPending
                ? t("financeEntries.reconciling")
                : "⚡ " + t("financeEntries.autoReconcileBtn")}
            </button>
          </>
        )}

        {/* Filtros */}
        <FilterSelect
          value={filters.company ?? ""}
          onChange={(v) => setFilters((f) => ({ ...f, company: v || undefined }))}
          opts={[{ value: "", label: t("financeEntries.filterAllCompanies") }, ...COMPANY_OPTS]}
        />
        <FilterSelect
          value={filters.type ?? ""}
          onChange={(v) => setFilters((f) => ({ ...f, type: v || undefined }))}
          opts={[{ value: "", label: t("financeEntries.filterAllTypes") }, ...TYPE_OPTS]}
        />
        <FilterSelect
          value={filters.account ?? ""}
          onChange={(v) => setFilters((f) => ({ ...f, account: v || undefined }))}
          opts={[{ value: "", label: t("financeEntries.filterAllAccounts") }, ...ACCOUNT_OPTS]}
        />
        <input
          className="input"
          style={{ width: 200 }}
          placeholder={t("financeEntries.searchPlaceholder")}
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
        />

        <span style={{ fontSize: 12, color: "var(--text3)", marginLeft: "auto" }}>
          {total}{" "}
          {total !== 1 ? t("financeEntries.entriesPlural") : t("financeEntries.entrySingular")}
        </span>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && canEdit && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(79,142,247,.08)",
            border: "1px solid rgba(79,142,247,.2)",
            borderRadius: "var(--r)",
            padding: "8px 14px",
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{ fontSize: 13, color: "var(--accent)", fontWeight: 500, whiteSpace: "nowrap" }}
          >
            {selected.size} {t("financeEntries.selectedCount")}
          </span>
          <span style={{ fontSize: 12, color: "var(--text3)" }}>
            {t("financeEntries.classifyAs")}
          </span>
          <FilterSelect
            value={bulkCompany}
            onChange={setBulkCompany}
            opts={[{ value: "", label: t("financeEntries.bulkOptCompany") }, ...COMPANY_OPTS]}
          />
          <FilterSelect
            value={bulkType}
            onChange={setBulkType}
            opts={[{ value: "", label: t("financeEntries.bulkOptType") }, ...TYPE_OPTS]}
          />
          <FilterSelect
            value={bulkIn}
            onChange={setBulkIn}
            opts={[{ value: "", label: t("financeEntries.bulkOptIn") }, ...ACCOUNT_OPTS]}
          />
          <FilterSelect
            value={bulkOut}
            onChange={setBulkOut}
            opts={[{ value: "", label: t("financeEntries.bulkOptOut") }, ...ACCOUNT_OPTS]}
          />
          <button
            className="btn primary"
            onClick={applyBulk}
            style={{ fontSize: 12, padding: "5px 12px" }}
          >
            ✓ {t("financeEntries.applyBtn")}
          </button>
          <button
            className="btn"
            onClick={() => setSelected(new Set())}
            style={{ fontSize: 12, padding: "5px 12px" }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div style={{ color: "var(--text3)", fontSize: 13 }}>{t("financeEntries.loading")}</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <div className="icon">📋</div>
          <p>
            {t("financeEntries.emptyState")}
            <br />
            {t("financeEntries.emptyStateHint")}
          </p>
        </div>
      ) : (
        <TopScrollTable>
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }}>
                  {canEdit && (
                    <input
                      type="checkbox"
                      checked={selected.size === items.length && items.length > 0}
                      onChange={(e) => toggleAll(e.target.checked)}
                      style={{ cursor: "pointer", accentColor: "var(--accent)" }}
                    />
                  )}
                </th>
                <th style={{ width: 28 }} />
                <th>{t("financeEntries.colDate")}</th>
                <th>{t("financeEntries.colDescription")}</th>
                <th>{t("financeEntries.colCompany")}</th>
                <th>{t("financeEntries.colType")}</th>
                <th>{t("financeEntries.colAccounts")}</th>
                <th>{t("financeEntries.colIn")}</th>
                <th>{t("financeEntries.colOut")}</th>
                <th style={{ width: 64 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <TransactionRow
                  key={r.id}
                  row={r}
                  selected={selected.has(r.id)}
                  onSelect={toggleSelect}
                  onUpdate={(field, val) => updateRow.mutate({ id: r.id, [field]: val })}
                  onReset={() => resetRow.mutate({ id: r.id })}
                  onDelete={async () => {
                    if (
                      await modals.confirm({
                        title: t("financeEntries.confirmTitle"),
                        message: t("financeEntries.confirmDeleteEntry"),
                        tone: "danger",
                      })
                    )
                      deleteRow.mutate({ id: r.id });
                  }}
                  onDistribute={() => setDistId(r.id)}
                />
              ))}
            </tbody>
          </table>
        </TopScrollTable>
      )}

      {/* Saldo final do mês */}
      {items.length > 0 && (
        <div
          style={{
            background: "var(--bg2)",
            border: "1px solid var(--border)",
            borderRadius: "var(--r2)",
            padding: "10px 14px",
            marginTop: 14,
          }}
        >
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: ".06em",
              color: "var(--text3)",
              marginBottom: 6,
            }}
          >
            {t("financeEntries.finalBalance")} ({monthLabel(month)})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {ACCOUNTS_LIST.map((a) => (
              <span key={a} style={{ fontSize: 12 }}>
                <span style={{ color: "var(--text3)" }}>{a}: </span>
                <span
                  style={{
                    fontFamily: "var(--mono)",
                    color:
                      (finalBal[a] ?? 0) > 0
                        ? "var(--green)"
                        : (finalBal[a] ?? 0) < 0
                          ? "var(--red)"
                          : "var(--text2)",
                  }}
                >
                  {f(finalBal[a] ?? 0)}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 16,
            justifyContent: "flex-end",
          }}
        >
          {page > 1 && (
            <button
              className="btn"
              style={{ padding: "5px 10px", fontSize: 12 }}
              onClick={() => setPage((p) => p - 1)}
            >
              ←
            </button>
          )}
          {Array.from({ length: pages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === pages || Math.abs(p - page) <= 1)
            .map((p, i, arr) => (
              <>
                {i > 0 && arr[i - 1] !== p - 1 && (
                  <span style={{ color: "var(--text3)", fontSize: 12 }}>…</span>
                )}
                <button
                  key={p}
                  className={`btn${p === page ? " primary" : ""}`}
                  style={{ padding: "5px 10px", fontSize: 12 }}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              </>
            ))}
          {page < pages && (
            <button
              className="btn"
              style={{ padding: "5px 10px", fontSize: 12 }}
              onClick={() => setPage((p) => p + 1)}
            >
              →
            </button>
          )}
        </div>
      )}

      {/* Distribution modal */}
      {distId && <DistribuirModal transactionId={distId} onClose={() => setDistId(null)} />}

      {/* Novo lançamento manual */}
      {showNovo && <NovoLancamentoModal defaultMonth={month} onClose={() => setShowNovo(false)} />}

      {/* Regras de conciliação automática */}
      {showRegras && <RegrasModal onClose={() => setShowRegras(false)} />}
    </div>
  );
}

function NovoLancamentoModal({
  defaultMonth,
  onClose,
}: {
  defaultMonth: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const modals = useModals();
  const t = useT();
  const create = trpc.finance.transactions.create.useMutation({
    onSuccess: () => {
      qc.invalidateQueries();
      onClose();
    },
  });
  const [date, setDate] = useState(defaultMonth + "-01");
  const [desc, setDesc] = useState("");
  const [company, setCompany] = useState("");
  const [type, setType] = useState("pending");
  const [accIn, setAccIn] = useState("");
  const [accOut, setAccOut] = useState("");
  const [amtIn, setAmtIn] = useState("");
  const [amtOut, setAmtOut] = useState("");

  // Transferência simétrica entre envelopes (Transf. interna ou Distribuição com
  // AMBAS as contas setadas): Entrada e Saída devem ser iguais. Os onChange dos
  // valores espelham automaticamente; o submit valida de novo (defesa em camadas).
  const isTransfer =
    (type === "internal_transfer" || type === "distribution") && !!accIn && !!accOut;

  function submit() {
    if (!desc || !date) {
      void modals.alert({
        title: t("financeEntries.warningTitle"),
        message: t("financeEntries.warnFillDateAndDescription"),
        tone: "warning",
      });
      return;
    }
    const aIn = parseFloat(amtIn) || 0;
    const aOut = parseFloat(amtOut) || 0;
    if (isTransfer && aIn !== aOut) {
      void modals.alert({
        title: t("financeEntries.warningTitle"),
        message: t("financeEntries.warnSymmetricTransfer"),
        tone: "warning",
      });
      return;
    }
    create.mutate({
      date,
      description: desc,
      company: (company || null) as any,
      type: type as any,
      accountIn: (accIn || null) as any,
      accountOut: (accOut || null) as any,
      amountIn: aIn,
      amountOut: aOut,
    });
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 540, maxWidth: "96vw" }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
          {t("financeEntries.newEntryTitle")}
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 12, marginBottom: 12 }}
        >
          <NLField label={t("financeEntries.colDate")}>
            <input
              className="input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </NLField>
          <NLField label={t("financeEntries.colDescription")}>
            <input
              className="input"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              autoFocus
            />
          </NLField>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <NLField label={t("financeEntries.colCompany")}>
            <select className="input" value={company} onChange={(e) => setCompany(e.target.value)}>
              <option value="">—</option>
              {COMPANY_OPTS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </NLField>
          <NLField label={t("financeEntries.colType")}>
            <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
              {TYPE_OPTS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </NLField>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <NLField label={t("financeEntries.fieldIn")}>
            <div style={{ display: "flex", gap: 6 }}>
              <select
                className="input"
                value={accIn}
                onChange={(e) => setAccIn(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">{t("financeEntries.optAccountPlaceholder")}</option>
                {ACCOUNT_OPTS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <input
                className="input"
                type="number"
                step={0.01}
                min={0}
                placeholder="0,00"
                value={amtIn}
                onChange={(e) => {
                  setAmtIn(e.target.value);
                  if (isTransfer) setAmtOut(e.target.value);
                }}
                style={{ width: 96 }}
              />
            </div>
          </NLField>
          <NLField label={t("financeEntries.fieldOut")}>
            <div style={{ display: "flex", gap: 6 }}>
              <select
                className="input"
                value={accOut}
                onChange={(e) => setAccOut(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">{t("financeEntries.optAccountPlaceholder")}</option>
                {ACCOUNT_OPTS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <input
                className="input"
                type="number"
                step={0.01}
                min={0}
                placeholder="0,00"
                value={amtOut}
                onChange={(e) => {
                  setAmtOut(e.target.value);
                  if (isTransfer) setAmtIn(e.target.value);
                }}
                style={{ width: 96 }}
              />
            </div>
          </NLField>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn" onClick={onClose}>
            {t("financeEntries.cancel")}
          </button>
          <button className="btn primary" onClick={submit} disabled={create.isPending || !desc}>
            {t("financeEntries.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function NLField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: "var(--text3)",
          marginBottom: 6,
          textTransform: "uppercase",
          letterSpacing: ".06em",
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function RegrasModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const t = useT();
  const { data: rules = [] } = trpc.finance.rules.list.useQuery();
  const create = trpc.finance.rules.create.useMutation({
    onSuccess: () => {
      qc.invalidateQueries();
      setPattern("");
      setErr("");
    },
  });
  const toggle = trpc.finance.rules.toggle.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const del = trpc.finance.rules.delete.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const [pattern, setPattern] = useState("");
  const [account, setAccount] = useState("Neto");
  const [err, setErr] = useState("");

  function add() {
    if (pattern.trim().split(/\s+/).filter(Boolean).length < 2) {
      setErr(t("financeEntries.errMinTwoWords"));
      return;
    }
    setErr("");
    create.mutate(
      { pattern: pattern.trim(), account: account as any },
      { onError: (e) => setErr(e.message) },
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 560, maxWidth: "96vw" }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
          ⚙ {t("financeEntries.rulesTitle")}
        </div>
        <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 16 }}>
          {t("financeEntries.rulesDescription")}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 6 }}>
          <div style={{ flex: 2 }}>
            <div
              style={{
                fontSize: 11,
                color: "var(--text3)",
                marginBottom: 4,
                textTransform: "uppercase",
                letterSpacing: ".06em",
              }}
            >
              {t("financeEntries.rulesPatternLabel")}
            </div>
            <input
              className="input"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder={t("financeEntries.rulesPatternPlaceholder")}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 11,
                color: "var(--text3)",
                marginBottom: 4,
                textTransform: "uppercase",
                letterSpacing: ".06em",
              }}
            >
              {t("financeEntries.accountLabel")}
            </div>
            <select className="input" value={account} onChange={(e) => setAccount(e.target.value)}>
              {ACCOUNT_OPTS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <button className="btn primary" onClick={add} disabled={create.isPending}>
            {t("financeEntries.addBtn")}
          </button>
        </div>
        {err && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 8 }}>{err}</div>}

        <div
          style={{
            marginTop: 10,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          {rules.length === 0 && (
            <div style={{ fontSize: 13, color: "var(--text3)", padding: "8px 0" }}>
              {t("financeEntries.noRulesYet")}
            </div>
          )}
          {rules.map((r) => (
            <div
              key={r.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "var(--bg3)",
                border: "1px solid var(--border)",
                borderRadius: "var(--r)",
                padding: "8px 12px",
                opacity: r.active ? 1 : 0.5,
              }}
            >
              <span style={{ flex: 1, fontSize: 13 }}>“{r.pattern}”</span>
              <span style={{ fontSize: 12, color: "var(--text3)" }}>→</span>
              <span style={{ fontFamily: "var(--mono)", fontSize: 13 }}>{r.account}</span>
              <button
                className="btn"
                style={{ fontSize: 11, padding: "3px 8px" }}
                onClick={() => toggle.mutate({ id: r.id, active: !r.active })}
              >
                {r.active ? t("financeEntries.ruleActive") : t("financeEntries.ruleInactive")}
              </button>
              <button
                className="btn danger"
                style={{ fontSize: 11, padding: "3px 8px" }}
                onClick={() => del.mutate({ id: r.id })}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
          <button className="btn" onClick={onClose}>
            {t("financeEntries.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TOP SCROLL BAR ──────────────────────────────────────────────
// Barra de rolagem horizontal no topo, sincronizada com a tabela,
// para mover as colunas ocultas sem precisar rolar até o rodapé.
function TopScrollTable({ children }: { children: ReactNode }) {
  const topRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const measure = () => setWidth(body.scrollWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(body);
    const table = body.querySelector("table");
    if (table) ro.observe(table);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      <div
        ref={topRef}
        className="table-scroll-top"
        onScroll={() => {
          if (bodyRef.current && topRef.current)
            bodyRef.current.scrollLeft = topRef.current.scrollLeft;
        }}
      >
        <div style={{ width }} />
      </div>
      <div
        ref={bodyRef}
        className="table-wrap"
        onScroll={() => {
          if (bodyRef.current && topRef.current)
            topRef.current.scrollLeft = bodyRef.current.scrollLeft;
        }}
      >
        {children}
      </div>
    </>
  );
}

// ─── ROW ─────────────────────────────────────────────────────────

function TransactionRow({
  row,
  selected,
  onSelect,
  onUpdate,
  onReset,
  onDelete,
  onDistribute,
}: {
  row: any;
  selected: boolean;
  onSelect: (id: string) => void;
  onUpdate: (field: string, val: any) => void;
  onReset: () => void;
  onDelete: () => void;
  onDistribute: () => void;
}) {
  const canEdit = useFinanceCanEdit();
  const t = useT();
  const isChild = !!row.parentId;
  const isPending = row.type === "pending";
  // ⚡ em qualquer linha-pai (não-filha) com valor — inclusive tipo 'distribution',
  // para sempre poder reabrir/editar a conciliação.
  const canDistribute =
    !isChild && ((Number(row.amountIn) || 0) > 0 || (Number(row.amountOut) || 0) > 0);

  return (
    <tr
      style={{
        background: selected ? "rgba(79,142,247,.06)" : isChild ? "rgba(79,142,247,.03)" : "",
        borderLeft: isChild ? "2px solid rgba(79,142,247,.15)" : "",
      }}
    >
      <td>
        {canEdit && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(row.id)}
            style={{ cursor: "pointer", accentColor: "var(--accent)" }}
          />
        )}
      </td>
      <td>
        <span className={`dot ${isPending ? "pending" : "ok"}`} />
      </td>
      <td className="mono" style={{ whiteSpace: "nowrap", fontSize: 12 }}>
        {row.date || "—"}
      </td>
      {/* F-releve-indy F4 (Tesouraria) — libellé limpo: contraparte em
          destaque, tipo+motif embaixo; o cru fica no tooltip. */}
      <td style={{ maxWidth: 220, overflow: "hidden" }} title={row.description}>
        {(() => {
          const p = parseBankLabel(row.description ?? "");
          const main = p.counterparty ?? bankLabelFallback(row.description);
          const sub = [p.kind ? t(BANK_KIND_LABEL_KEYS[p.kind]) : null, p.motif]
            .filter(Boolean)
            .join(" · ");
          return (
            <>
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontWeight: 600,
                }}
              >
                {main}
              </div>
              {sub && (
                <div
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: 10,
                    opacity: 0.65,
                  }}
                >
                  {sub}
                </div>
              )}
            </>
          );
        })()}
      </td>
      <td>
        <InlineSel
          value={row.company ?? ""}
          onChange={(v) => onUpdate("company", v || null)}
          opts={[{ value: "", label: "—" }, ...COMPANY_OPTS]}
        />
      </td>
      <td>
        <InlineSel value={row.type} onChange={(v) => onUpdate("type", v)} opts={[...TYPE_OPTS]} />
      </td>
      <td style={{ display: "flex", gap: 4 }}>
        {row.hasChildren ? (
          // Pai de split: contas são geridas pelas filhas. Editar inline quebrava
          // a conservação (caso AXA: pai com accountOut setado + filhas = dupla
          // contagem). Mostra só leitura; edição pelo ⚡ (que mantém o Σ).
          <>
            <span
              title={t("financeEntries.distParentAccountTitle")}
              style={{
                fontSize: 12,
                color: "var(--text3)",
                padding: "5px 8px",
                whiteSpace: "nowrap",
                fontStyle: "italic",
              }}
            >
              ↑ {row.accountOut ?? "—"}
            </span>
            <span
              title={t("financeEntries.distParentAccountTitle")}
              style={{
                fontSize: 12,
                color: "var(--text3)",
                padding: "5px 8px",
                whiteSpace: "nowrap",
                fontStyle: "italic",
              }}
            >
              ↓ {row.accountIn ?? "—"}
            </span>
          </>
        ) : (
          <>
            <InlineSel
              value={row.accountOut ?? ""}
              onChange={(v) => onUpdate("accountOut", v || null)}
              opts={[
                { value: "", label: "↑ —" },
                ...ACCOUNT_OPTS.map((o) => ({ ...o, label: "↑ " + o.label })),
              ]}
            />
            <InlineSel
              value={row.accountIn ?? ""}
              onChange={(v) => onUpdate("accountIn", v || null)}
              opts={[
                { value: "", label: "↓ —" },
                ...ACCOUNT_OPTS.map((o) => ({ ...o, label: "↓ " + o.label })),
              ]}
            />
          </>
        )}
      </td>
      <td className={Number(row.amountIn) > 0 ? "val-pos" : "val-zero"}>
        {Number(row.amountIn) > 0 ? fmt(Number(row.amountIn)) : "—"}
      </td>
      <td className={Number(row.amountOut) > 0 ? "val-neg" : "val-zero"}>
        {Number(row.amountOut) > 0 ? fmt(Number(row.amountOut)) : "—"}
      </td>
      <td style={{ display: "flex", gap: 4, alignItems: "center" }}>
        {canEdit && canDistribute && (
          <button
            className="btn"
            style={{
              padding: "3px 8px",
              fontSize: 13,
              color: row.isDistributed ? "var(--text3)" : "var(--green)",
              opacity: row.isDistributed ? 0.5 : 1,
            }}
            onClick={onDistribute}
            title={
              row.isDistributed
                ? t("financeEntries.redistributeTitle")
                : t("financeEntries.distributeTitle")
            }
          >
            ⚡
          </button>
        )}
        {isChild ? (
          <span
            style={{ fontSize: 11, color: "var(--text3)", whiteSpace: "nowrap" }}
            title={t("financeEntries.childManagedTitle")}
          >
            ↳ {t("financeEntries.distributionChild")}
          </span>
        ) : !canEdit ? null : row.imported ? (
          <button
            className="btn"
            style={{ padding: "3px 8px", fontSize: 13, color: "var(--amber)" }}
            onClick={onReset}
            title={t("financeEntries.unreconcileTitle")}
          >
            ↺
          </button>
        ) : (
          <button
            className="btn danger"
            style={{ padding: "3px 8px", fontSize: 11 }}
            onClick={onDelete}
            title={t("financeEntries.deletePermanentTitle")}
          >
            ✕
          </button>
        )}
      </td>
    </tr>
  );
}

// ─── INLINE SELECT ────────────────────────────────────────────────

function InlineSel({
  value,
  onChange,
  opts,
}: {
  value: string;
  onChange: (v: string) => void;
  opts: { value: string; label: string }[];
}) {
  const canEdit = useFinanceCanEdit();
  // Viewer (sócio): só leitura — mostra o rótulo atual, sem dropdown editável.
  if (!canEdit) {
    const current = opts.find((o) => o.value === value);
    return (
      <span
        style={{ fontSize: 12, color: "var(--text2)", padding: "5px 8px", whiteSpace: "nowrap" }}
      >
        {current?.label ?? "—"}
      </span>
    );
  }
  return (
    <select className="inline-sel" value={value} onChange={(e) => onChange(e.target.value)}>
      {opts.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function FilterSelect({
  value,
  onChange,
  opts,
}: {
  value: string;
  onChange: (v: string) => void;
  opts: { value: string; label: string }[];
}) {
  return (
    <select
      style={{
        background: "var(--bg3)",
        border: "1px solid var(--border2)",
        color: "var(--text)",
        fontFamily: "var(--sans)",
        fontSize: 13,
        padding: "7px 10px",
        borderRadius: "var(--r)",
        cursor: "pointer",
      }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {opts.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ─── DISTRIBUTION MODAL ───────────────────────────────────────────

const DIST_TARGETS = [
  { value: "Neto", label: "Neto" },
  { value: "Landione", label: "Landione" },
  { value: "Pagar Técnico", label: "Pagar Técnico" },
] as const;

type DistLine = { id: string; account: string; label: string; amount: string };

function DistribuirModal({
  transactionId,
  onClose,
}: {
  transactionId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const modals = useModals();
  const t = useT();
  const parentQ = trpc.finance.transactions.byId.useQuery({ id: transactionId });
  const childrenQ = trpc.finance.transactions.children.useQuery({ parentId: transactionId });
  const parent = parentQ.data;
  const children = childrenQ.data ?? [];
  const save = trpc.finance.distribution.save.useMutation({
    onSuccess: (res) => {
      qc.invalidateQueries();
      if (res.warning)
        void modals.alert({
          title: t("financeEntries.warningTitle"),
          message: "⚠️ " + res.warning,
          tone: "warning",
        });
      onClose();
    },
  });

  const amtIn = Number(parent?.amountIn) || 0;
  const amtOut = Number(parent?.amountOut) || 0;
  const isRevenue = amtIn >= amtOut && amtIn > 0;
  const total = isRevenue ? amtIn : amtOut;
  const company = parent?.company; // etiqueta de banco (BR/Euro), só informativa

  const [lines, setLines] = useState<DistLine[]>([]);

  // Inicializa as linhas UMA vez, quando parent E children já carregaram. As queries são
  // assíncronas: sem esperar por elas, o modal abria com a linha-default e PERDIA as
  // conciliações já feitas (filhos de distribuição ou conta direta do lançamento).
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current || !parentQ.isSuccess || !childrenQ.isSuccess || !parent) return;
    initRef.current = true;

    if (children.length > 0) {
      setLines(
        children.map((c) => {
          const target = (isRevenue ? c.accountIn : c.accountOut) ?? "Neto";
          const m = c.description.match(/·\s*(.+?)\]/);
          return {
            id: crypto.randomUUID(),
            account: target as string,
            label: m?.[1] ?? "",
            amount: String(isRevenue ? c.amountIn : c.amountOut),
          };
        }),
      );
      return;
    }
    const ownAcc = isRevenue ? parent.accountIn : parent.accountOut;
    if (parent.isDistributed && ownAcc) {
      setLines([{ id: crypto.randomUUID(), account: ownAcc, label: "", amount: String(total) }]);
      return;
    }
    setLines([
      { id: crypto.randomUUID(), account: "Neto", label: "", amount: total ? String(total) : "" },
    ]);
  }, [parentQ.isSuccess, childrenQ.isSuccess]);

  const alocado = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const residual = total - alocado;
  const ok = residual >= -0.01;

  function updateLine(id: string, field: keyof DistLine, val: string) {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: val } : l)));
  }

  function confirm() {
    const valid = lines.filter((l) => parseFloat(l.amount) > 0);
    if (valid.length === 0) {
      void modals.alert({
        title: t("financeEntries.warningTitle"),
        message: t("financeEntries.warnAddAtLeastOneLine"),
        tone: "warning",
      });
      return;
    }
    if (alocado > total + 0.01) {
      void modals.alert({
        title: t("financeEntries.warningTitle"),
        message: t("financeEntries.warnAllocatedExceeds"),
        tone: "warning",
      });
      return;
    }
    save.mutate({
      parentId: transactionId,
      lines: valid.map((l) => ({
        account: l.account as any,
        label: l.label || undefined,
        amount: parseFloat(l.amount),
      })),
    });
  }

  if (!parent) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          maxWidth: "96vw",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
          ⚡{" "}
          {isRevenue
            ? t("financeEntries.distributeRevenueTitle")
            : t("financeEntries.distributeExpenseTitle")}
        </div>
        <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 16 }}>
          {isRevenue ? t("financeEntries.distRevenueHint") : t("financeEntries.distExpenseHint")}
        </div>

        <div
          style={{
            background: "var(--bg3)",
            borderRadius: "var(--r)",
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          <span style={{ color: "var(--text2)" }}>
            {parent.date} · {parent.description.slice(0, 60)}
          </span>
          <br />
          <span
            style={{
              fontSize: 18,
              fontWeight: 500,
              fontFamily: "var(--mono)",
              color: isRevenue ? "var(--green)" : "var(--red)",
            }}
          >
            {fmt(total)}
          </span>
          <span style={{ color: "var(--text3)" }}>
            {" "}
            {isRevenue ? t("financeEntries.toDistribute") : t("financeEntries.toReconcile")} ·{" "}
            {t("financeEntries.bankLabel")}: {company ?? "—"}
          </span>
          {children.length > 0 && (
            <span
              style={{
                marginLeft: 10,
                fontSize: 11,
                padding: "2px 8px",
                background: "rgba(79,142,247,.15)",
                color: "var(--accent)",
                borderRadius: 4,
              }}
            >
              ✏ {t("financeEntries.editing")}
            </span>
          )}
        </div>

        <div
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: ".08em",
            color: "var(--text3)",
            marginBottom: 10,
          }}
        >
          {isRevenue ? t("financeEntries.destinations") : t("financeEntries.sources")}
        </div>

        {/* Área rolável quando há muitas linhas — rodapé (resumo+botões) fica fixo */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            marginRight: -4,
            paddingRight: 4,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {lines.map((l) => (
              <div
                key={l.id}
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  background: "var(--bg3)",
                  borderRadius: "var(--r)",
                  padding: "8px 10px",
                }}
              >
                <select
                  className="inline-sel"
                  style={{ flex: 1.4, maxWidth: "none", background: "var(--bg4)" }}
                  value={l.account}
                  onChange={(e) => updateLine(l.id, "account", e.target.value)}
                >
                  {(isRevenue ? DIST_TARGETS : ACCOUNT_OPTS).map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <div style={{ flex: 1 }} />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    background: "var(--bg4)",
                    border: "1px solid var(--border2)",
                    borderRadius: "var(--r)",
                    padding: "0 8px",
                    height: 30,
                  }}
                >
                  <span style={{ fontSize: 12, color: "var(--text3)" }}>€</span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder={t("financeEntries.amountPlaceholder")}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--text)",
                      fontFamily: "var(--mono)",
                      fontSize: 13,
                      width: 90,
                      outline: "none",
                    }}
                    value={l.amount}
                    onChange={(e) => updateLine(l.id, "amount", e.target.value)}
                  />
                </div>
                <button
                  onClick={() => setLines((prev) => prev.filter((x) => x.id !== l.id))}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--text3)",
                    cursor: "pointer",
                    fontSize: 18,
                    padding: "0 4px",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <button
            disabled={residual < 0.01}
            onClick={() => {
              if (residual >= 0.01)
                setLines((prev) => [
                  ...prev,
                  { id: crypto.randomUUID(), account: "Neto", label: "", amount: "" },
                ]);
            }}
            title={residual < 0.01 ? t("financeEntries.residualZeroTitle") : ""}
            style={{
              marginTop: 8,
              background: "none",
              border: "1px dashed var(--border2)",
              color: "var(--text3)",
              borderRadius: "var(--r)",
              padding: "6px 12px",
              fontSize: 12,
              cursor: residual < 0.01 ? "not-allowed" : "pointer",
              width: "100%",
              opacity: residual < 0.01 ? 0.4 : 1,
            }}
          >
            + {isRevenue ? t("financeEntries.addDestination") : t("financeEntries.addSource")}
          </button>
        </div>

        {/* Resumo */}
        <div
          style={{
            marginTop: 14,
            background: "var(--bg3)",
            borderRadius: "var(--r)",
            padding: "12px 14px",
            fontSize: 13,
            lineHeight: 1.9,
          }}
        >
          {[
            [t("financeEntries.entryTotal"), fmt(total), isRevenue ? "var(--green)" : "var(--red)"],
            [t("financeEntries.allocated"), fmt(alocado), "var(--text)"],
          ].map(([lab, v, c]) => (
            <div
              key={lab as string}
              style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}
            >
              <span style={{ color: "var(--text2)" }}>{lab}</span>
              <span style={{ fontFamily: "var(--mono)", color: c as string }}>{v}</span>
            </div>
          ))}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              borderTop: "1px solid var(--border)",
              paddingTop: 6,
              marginTop: 4,
            }}
          >
            <span style={{ color: "var(--text2)" }}>
              {isRevenue ? t("financeEntries.residualTo") : t("financeEntries.residualFrom")}{" "}
              {/* "Despesas" é o VALOR da conta virtual (ACCOUNT_OPTS), não copy — não traduz. */}
              Despesas
            </span>
            <span
              style={{
                fontFamily: "var(--mono)",
                fontWeight: 500,
                color: ok ? "var(--text)" : "var(--red)",
              }}
            >
              {fmt(residual)}
              {ok ? "" : " " + t("financeEntries.exceeds")}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
          <button className="btn" onClick={onClose}>
            {t("financeEntries.cancel")}
          </button>
          <button className="btn primary" onClick={confirm} disabled={save.isPending || !ok}>
            {children.length > 0
              ? "✓ " + t("financeEntries.saveChanges")
              : "✓ " + t("financeEntries.distributeBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
